/* eslint-disable @typescript-eslint/no-use-before-define */

const WORLD_WIDTH = 390;
const WORLD_HEIGHT = 844;
const TAU = Math.PI * 2;

const COLORS = {
  void: "#03050a",
  navy: "#07121c",
  panel: "#0c1a23",
  cyan: "#62f5dc",
  cyanDark: "#168f83",
  cream: "#fff1c4",
  amber: "#ffbf57",
  orange: "#ff7b45",
  laser: "#ff3e72",
  laserHot: "#fff0f5",
  violet: "#8f68ff",
  muted: "#729296"
};

const HACK_TARGETS = [0.32, 0.66, 0.47];
const HACK_SPEEDS = [0.72, 0.92, 1.08];
const HACK_ZONE_WIDTHS = [0.2, 0.17, 0.14];

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function distance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function roundRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function drawText(ctx, text, x, y, options) {
  const opts = options || {};
  ctx.save();
  ctx.font =
    opts.font || '700 15px "Arial Narrow", "Noto Sans Thai", sans-serif';
  ctx.fillStyle = opts.color || "#ffffff";
  ctx.textAlign = opts.align || "center";
  ctx.textBaseline = opts.baseline || "middle";
  if (opts.shadow) {
    ctx.shadowColor = opts.shadow;
    ctx.shadowBlur = opts.shadowBlur || 12;
  }
  if (opts.letterSpacing) {
    const characters = String(text).split("");
    const widths = characters.map(
      character => ctx.measureText(character).width
    );
    const total =
      widths.reduce((sum, width) => sum + width, 0) +
      opts.letterSpacing * (characters.length - 1);
    let cursor = x - total / 2;
    characters.forEach((character, index) => {
      const width = widths[index];
      ctx.fillText(character, cursor + width / 2, y);
      cursor += width + opts.letterSpacing;
    });
  } else {
    ctx.fillText(text, x, y);
  }
  ctx.restore();
}

function radialGradient(ctx, x, y, radius, centerColor, edgeColor) {
  const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
  gradient.addColorStop(0, centerColor);
  gradient.addColorStop(1, edgeColor);
  return gradient;
}

function seededRandom(seedState) {
  seedState.value = (seedState.value * 1664525 + 1013904223) >>> 0;
  return seedState.value / 4294967296;
}

function createAudioEngine() {
  let context = null;
  let lastStepAt = 0;

  function ensureContext() {
    if (context) {
      if (context.state === "suspended") context.resume();
      return context;
    }
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return null;
    context = new AudioContext();
    return context;
  }

  function tone(frequency, duration, volume, type, delay, endFrequency) {
    const audioContext = ensureContext();
    if (!audioContext) return;
    const start = audioContext.currentTime + (delay || 0);
    const oscillator = audioContext.createOscillator();
    const gain = audioContext.createGain();
    oscillator.type = type || "sine";
    oscillator.frequency.setValueAtTime(frequency, start);
    if (endFrequency) {
      oscillator.frequency.exponentialRampToValueAtTime(
        endFrequency,
        start + duration
      );
    }
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(volume || 0.025, start + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain);
    gain.connect(audioContext.destination);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.03);
  }

  return {
    unlock() {
      ensureContext();
    },
    step() {
      const now = performance.now();
      if (now - lastStepAt < 145) return;
      lastStepAt = now;
      tone(95, 0.045, 0.012, "triangle");
    },
    alert() {
      tone(180, 0.15, 0.045, "sawtooth", 0, 95);
    },
    action() {
      tone(520, 0.08, 0.025, "square");
    },
    hackSuccess(index) {
      tone(420 + index * 130, 0.16, 0.04, "sine");
      tone(680 + index * 120, 0.2, 0.035, "sine", 0.08);
    },
    hackMiss() {
      tone(150, 0.22, 0.04, "sawtooth", 0, 80);
    },
    unlockDoor() {
      tone(110, 0.42, 0.06, "triangle", 0, 62);
      tone(720, 0.34, 0.035, "sine", 0.12);
    },
    steal() {
      tone(330, 0.35, 0.04, "sine");
      tone(660, 0.45, 0.045, "sine", 0.12);
      tone(990, 0.52, 0.035, "sine", 0.25);
    },
    win() {
      tone(392, 0.4, 0.045, "sine");
      tone(523, 0.44, 0.04, "sine", 0.12);
      tone(784, 0.65, 0.045, "sine", 0.26);
    },
    destroy() {
      if (context && typeof context.close === "function") context.close();
      context = null;
    }
  };
}

function createRain(seedState) {
  return Array.from({ length: 46 }, (_, index) => ({
    x: seededRandom(seedState) * WORLD_WIDTH,
    y: seededRandom(seedState) * WORLD_HEIGHT,
    length: 4 + seededRandom(seedState) * 15,
    speed: 58 + seededRandom(seedState) * 105,
    alpha: 0.08 + seededRandom(seedState) * 0.24,
    phase: index * 0.33
  }));
}

export function createNeonHeistGame(canvas, options) {
  const opts = options || {};
  const ctx = canvas.getContext("2d");
  const audio = createAudioEngine();
  const seedState = { value: 8882026 };
  const rain = createRain(seedState);
  const keys = new Set();
  const state = {
    mode: "intro",
    phase: "infiltrate",
    paused: false,
    elapsed: 0,
    missionTime: 42,
    alarmTime: 18,
    integrity: 5,
    maxIntegrity: 5,
    damageCooldown: 0,
    damageFlash: 0,
    score: 0,
    terminalUnlocked: false,
    artifactTaken: false,
    player: {
      x: 195,
      y: 652,
      radius: 11,
      angle: -Math.PI / 2,
      moving: false
    },
    drone: {
      x: 72,
      y: 232,
      radius: 13,
      active: false,
      pulse: 0
    },
    joystick: {
      active: false,
      pointerId: null,
      x: 0,
      y: 0
    },
    hack: {
      hits: 0,
      misses: 0,
      cursor: 0,
      direction: 1,
      flash: 0,
      result: ""
    },
    particles: [],
    message: "แตะเพื่อรับภารกิจ",
    prompt: "",
    completedScore: 0
  };

  const viewport = {
    width: WORLD_WIDTH,
    height: WORLD_HEIGHT,
    dpr: 1,
    scale: 1,
    offsetX: 0,
    offsetY: 0
  };

  let animationFrame = 0;
  let previousTimestamp = performance.now();
  let destroyed = false;

  function vibrate(pattern) {
    if (navigator.vibrate) navigator.vibrate(pattern);
  }

  function resize() {
    const bounds = canvas.getBoundingClientRect();
    viewport.width = Math.max(1, bounds.width || window.innerWidth);
    viewport.height = Math.max(1, bounds.height || window.innerHeight);
    viewport.dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(viewport.width * viewport.dpr);
    canvas.height = Math.round(viewport.height * viewport.dpr);
    viewport.scale = Math.min(
      viewport.width / WORLD_WIDTH,
      viewport.height / WORLD_HEIGHT
    );
    viewport.offsetX = (viewport.width - WORLD_WIDTH * viewport.scale) / 2;
    viewport.offsetY = (viewport.height - WORLD_HEIGHT * viewport.scale) / 2;
    render();
  }

  function toWorld(clientX, clientY) {
    const bounds = canvas.getBoundingClientRect();
    return {
      x:
        (clientX - bounds.left - viewport.offsetX) /
        Math.max(viewport.scale, 0.001),
      y:
        (clientY - bounds.top - viewport.offsetY) /
        Math.max(viewport.scale, 0.001)
    };
  }

  function pointInRect(point, x, y, width, height) {
    return (
      point.x >= x &&
      point.x <= x + width &&
      point.y >= y &&
      point.y <= y + height
    );
  }

  function pointInCircle(point, x, y, radius) {
    return Math.hypot(point.x - x, point.y - y) <= radius;
  }

  function getWalls() {
    const walls = [
      { x: 135, y: 512, width: 28, height: 112 },
      { x: 268, y: 365, width: 27, height: 122 },
      { x: 28, y: 402, width: 64, height: 25 },
      { x: 298, y: 545, width: 62, height: 24 }
    ];
    if (state.terminalUnlocked) {
      walls.push(
        { x: 18, y: 296, width: 119, height: 18 },
        { x: 253, y: 296, width: 119, height: 18 }
      );
    } else {
      walls.push({ x: 18, y: 296, width: 354, height: 18 });
    }
    return walls;
  }

  function circleHitsRect(x, y, radius, rect) {
    const closestX = clamp(x, rect.x, rect.x + rect.width);
    const closestY = clamp(y, rect.y, rect.y + rect.height);
    return Math.hypot(x - closestX, y - closestY) < radius;
  }

  function playerPositionBlocked(x, y) {
    if (x < 29 || x > 361 || y < 168 || y > 672) return true;
    return getWalls().some(wall =>
      circleHitsRect(x, y, state.player.radius, wall)
    );
  }

  function getLaserState() {
    const speedMultiplier = state.phase === "escape" ? 1.5 : 1;
    const firstGap =
      195 + Math.sin(state.elapsed * 1.42 * speedMultiplier) * 82;
    const secondGap =
      195 + Math.sin(state.elapsed * 1.76 * speedMultiplier + 2.6) * 94;
    const verticalX =
      218 + Math.sin(state.elapsed * 1.9 * speedMultiplier + 0.8) * 63;
    const verticalActive =
      Math.sin(state.elapsed * 2.7 * speedMultiplier) > -0.62;
    return [
      {
        id: "lower-horizontal",
        type: "horizontal",
        y: 574,
        x1: 28,
        x2: 361,
        gapCenter: firstGap,
        gapWidth: 72,
        active: true
      },
      {
        id: "upper-horizontal",
        type: "horizontal",
        y: 474,
        x1: 28,
        x2: 361,
        gapCenter: secondGap,
        gapWidth: 66,
        active: true
      },
      {
        id: "vertical-sweep",
        type: "vertical",
        x: verticalX,
        y1: 318,
        y2: 455,
        active: verticalActive
      }
    ];
  }

  function objectiveText() {
    if (state.mode === "hacking") return "SYNC 3 ช่องเพื่อปลดล็อกประตู";
    if (state.phase === "infiltrate") return "ฝ่าเลเซอร์ไปยังจุด HACK";
    if (state.phase === "artifact") return "เข้าห้องนิรภัยและชิง PRISM 888";
    if (state.phase === "escape") return "กลับไปยังทางออกก่อนสัญญาณเตือนหมด";
    return "";
  }

  function terminalPosition() {
    return { x: 325, y: 342 };
  }

  function artifactPosition() {
    return { x: 195, y: 223 };
  }

  function exitPosition() {
    return { x: 195, y: 660 };
  }

  function spawnParticles(count, x, y, colors, speedScale) {
    for (let index = 0; index < count; index += 1) {
      const angle = seededRandom(seedState) * TAU;
      const speed = (28 + seededRandom(seedState) * 105) * (speedScale || 1);
      state.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: 1.2 + seededRandom(seedState) * 3.7,
        life: 0.6 + seededRandom(seedState) * 1.3,
        maxLife: 1.9,
        color:
          colors[Math.floor(seededRandom(seedState) * colors.length)] ||
          COLORS.cyan
      });
    }
  }

  function startGame() {
    audio.unlock();
    state.mode = "playing";
    state.phase = "infiltrate";
    state.paused = false;
    state.elapsed = 0;
    state.missionTime = 42;
    state.alarmTime = 18;
    state.integrity = 5;
    state.damageCooldown = 0;
    state.damageFlash = 0;
    state.score = 0;
    state.terminalUnlocked = false;
    state.artifactTaken = false;
    state.player.x = 195;
    state.player.y = 652;
    state.player.angle = -Math.PI / 2;
    state.player.moving = false;
    state.drone.x = 72;
    state.drone.y = 232;
    state.drone.active = false;
    state.joystick.active = false;
    state.hack.hits = 0;
    state.hack.misses = 0;
    state.hack.cursor = 0;
    state.hack.direction = 1;
    state.hack.flash = 0;
    state.hack.result = "";
    state.particles = [];
    state.prompt = "";
    state.message = "แทรกซึมไปยังเทอร์มินัล";
    state.completedScore = 0;
    vibrate(18);
  }

  function lose(reason) {
    if (state.mode === "lost" || state.mode === "win") return;
    state.mode = "lost";
    state.paused = false;
    state.message = reason;
    state.player.moving = false;
    state.joystick.active = false;
    keys.clear();
    audio.alert();
    vibrate([80, 50, 130]);
    if (typeof opts.onFail === "function") opts.onFail(reason);
  }

  function winGame() {
    if (state.mode === "win") return;
    const timeBonus = Math.max(0, Math.round(state.alarmTime * 60));
    state.completedScore =
      1200 + timeBonus + state.integrity * 280 - state.hack.misses * 120;
    state.score = Math.max(state.score, state.completedScore);
    state.mode = "win";
    state.message = "PRISM 888 ถูกส่งออกอย่างปลอดภัย";
    state.player.moving = false;
    state.joystick.active = false;
    keys.clear();
    spawnParticles(82, 195, 380, [
      COLORS.cyan,
      COLORS.amber,
      COLORS.cream,
      COLORS.violet
    ]);
    audio.win();
    vibrate([30, 35, 30, 35, 120]);
    if (typeof opts.onComplete === "function") {
      opts.onComplete(state.completedScore);
    }
  }

  function damagePlayer(reason) {
    if (state.damageCooldown > 0 || state.mode !== "playing") return;
    state.integrity -= 1;
    state.damageCooldown = 1.05;
    state.damageFlash = 1;
    state.score = Math.max(0, state.score - 90);
    state.message = reason;
    spawnParticles(18, state.player.x, state.player.y, [
      COLORS.laser,
      COLORS.cream,
      COLORS.orange
    ]);
    audio.alert();
    vibrate([32, 30, 54]);
    if (state.integrity <= 0) lose("ระบบรักษาความปลอดภัยจับตำแหน่งได้");
  }

  function checkLaserCollisions() {
    getLaserState().forEach(laser => {
      if (!laser.active) return;
      if (laser.type === "horizontal") {
        const nearBeam =
          Math.abs(state.player.y - laser.y) < state.player.radius + 3;
        const outsideGap =
          Math.abs(state.player.x - laser.gapCenter) > laser.gapWidth / 2;
        if (nearBeam && outsideGap) {
          damagePlayer("เลเซอร์ตรวจจับ — Integrity ลดลง");
        }
      } else {
        const nearBeam =
          Math.abs(state.player.x - laser.x) < state.player.radius + 3;
        const withinSpan =
          state.player.y > laser.y1 - state.player.radius &&
          state.player.y < laser.y2 + state.player.radius;
        if (nearBeam && withinSpan) {
          damagePlayer("โดนเลเซอร์กวาดพื้นที่");
        }
      }
    });
  }

  function getMovementVector() {
    let x = 0;
    let y = 0;
    if (keys.has("arrowleft") || keys.has("a")) x -= 1;
    if (keys.has("arrowright") || keys.has("d")) x += 1;
    if (keys.has("arrowup") || keys.has("w")) y -= 1;
    if (keys.has("arrowdown") || keys.has("s")) y += 1;
    if (state.joystick.active) {
      x += state.joystick.x;
      y += state.joystick.y;
    }
    const magnitude = Math.hypot(x, y);
    if (magnitude > 1) {
      x /= magnitude;
      y /= magnitude;
    }
    return { x, y };
  }

  function updatePrompt() {
    const terminal = terminalPosition();
    const artifact = artifactPosition();
    if (state.phase === "infiltrate" && distance(state.player, terminal) < 43) {
      state.prompt = "HACK";
    } else if (
      state.phase === "artifact" &&
      distance(state.player, artifact) < 42
    ) {
      state.prompt = "TAKE";
    } else {
      state.prompt = "";
    }
  }

  function updatePlayer(dt) {
    const movement = getMovementVector();
    const speed = state.phase === "escape" ? 148 : 132;
    const moving = Math.hypot(movement.x, movement.y) > 0.06;
    state.player.moving = moving;
    if (!moving) {
      updatePrompt();
      return;
    }

    state.player.angle = Math.atan2(movement.y, movement.x);
    const nextX = state.player.x + movement.x * speed * dt;
    const nextY = state.player.y + movement.y * speed * dt;
    if (!playerPositionBlocked(nextX, state.player.y)) {
      state.player.x = nextX;
    }
    if (!playerPositionBlocked(state.player.x, nextY)) {
      state.player.y = nextY;
    }
    audio.step();
    updatePrompt();
  }

  function updateDrone(dt) {
    if (!state.drone.active || state.mode !== "playing") return;
    const dx = state.player.x - state.drone.x;
    const dy = state.player.y - state.drone.y;
    const length = Math.max(1, Math.hypot(dx, dy));
    const speed = 51 + (18 - state.alarmTime) * 1.7;
    const targetX = state.drone.x + (dx / length) * speed * dt;
    const targetY = state.drone.y + (dy / length) * speed * dt;
    if (!getWalls().some(wall => circleHitsRect(targetX, targetY, 11, wall))) {
      state.drone.x = targetX;
      state.drone.y = targetY;
    } else {
      state.drone.x += Math.sin(state.elapsed * 5) * speed * dt;
      state.drone.y += Math.cos(state.elapsed * 4) * speed * dt;
    }
    state.drone.pulse = 0.5 + Math.sin(state.elapsed * 7) * 0.5;
    if (distance(state.player, state.drone) < 25) {
      damagePlayer("โดรนรักษาความปลอดภัยโจมตี");
    }
  }

  function updateParticles(dt) {
    state.particles.forEach(particle => {
      particle.life -= dt;
      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;
      particle.vx *= Math.pow(0.97, dt * 60);
      particle.vy *= Math.pow(0.97, dt * 60);
    });
    state.particles = state.particles.filter(particle => particle.life > 0);
  }

  function updateHack(dt) {
    const index = Math.min(state.hack.hits, HACK_SPEEDS.length - 1);
    state.hack.cursor += HACK_SPEEDS[index] * state.hack.direction * dt;
    if (state.hack.cursor >= 1) {
      state.hack.cursor = 1;
      state.hack.direction = -1;
    } else if (state.hack.cursor <= 0) {
      state.hack.cursor = 0;
      state.hack.direction = 1;
    }
    state.hack.flash = Math.max(0, state.hack.flash - dt * 1.8);
  }

  function performHackSync() {
    if (state.mode !== "hacking") return;
    const index = state.hack.hits;
    const target = HACK_TARGETS[index];
    const zoneWidth = HACK_ZONE_WIDTHS[index];
    const accurate = Math.abs(state.hack.cursor - target) <= zoneWidth / 2;

    if (accurate) {
      state.hack.hits += 1;
      state.hack.flash = 1;
      state.hack.result = "SYNC";
      state.score += 240 + index * 90;
      spawnParticles(24, 195, 433, [COLORS.cyan, COLORS.cream, COLORS.amber]);
      audio.hackSuccess(index);
      vibrate([18, 20, 24]);
      if (state.hack.hits >= 3) {
        state.terminalUnlocked = true;
        state.phase = "artifact";
        state.mode = "playing";
        state.missionTime += 5;
        state.message = "ประตูเปิดแล้ว — เข้าไปชิง PRISM 888";
        state.prompt = "";
        audio.unlockDoor();
        vibrate([40, 30, 70]);
      } else {
        state.hack.cursor = 0;
        state.hack.direction = 1;
        state.message = `SYNC สำเร็จ ${state.hack.hits}/3`;
      }
    } else {
      state.hack.misses += 1;
      state.hack.flash = 1;
      state.hack.result = "MISS";
      state.integrity -= 1;
      state.message = "จังหวะผิด — ระบบตรวจจับเพิ่มขึ้น";
      audio.hackMiss();
      vibrate(55);
      if (state.integrity <= 0) lose("แฮกไม่สำเร็จ ระบบล็อกถาวร");
    }
  }

  function performAction() {
    audio.unlock();
    if (state.mode === "intro") {
      startGame();
      return;
    }
    if (state.mode === "win" || state.mode === "lost") {
      startGame();
      return;
    }
    if (state.mode === "hacking") {
      performHackSync();
      return;
    }
    if (state.mode !== "playing") return;

    const terminal = terminalPosition();
    const artifact = artifactPosition();
    if (state.phase === "infiltrate" && distance(state.player, terminal) < 46) {
      state.mode = "hacking";
      state.player.moving = false;
      state.hack.cursor = 0;
      state.hack.direction = 1;
      state.message = "แตะ SYNC ให้ตรงกับช่องสัญญาณ";
      state.prompt = "";
      audio.action();
      vibrate(18);
      return;
    }
    if (state.phase === "artifact" && distance(state.player, artifact) < 45) {
      state.phase = "escape";
      state.artifactTaken = true;
      state.drone.active = true;
      state.drone.x = 62;
      state.drone.y = 232;
      state.alarmTime = 18;
      state.score += 888;
      state.message = "ALARM — กลับไปยังทางออก!";
      state.prompt = "";
      spawnParticles(55, artifact.x, artifact.y, [
        COLORS.cyan,
        COLORS.violet,
        COLORS.cream
      ]);
      audio.steal();
      vibrate([35, 30, 35, 30, 80]);
    }
  }

  function update(dt) {
    const safeDelta = clamp(dt, 0, 0.05);
    state.elapsed += safeDelta;
    state.damageCooldown = Math.max(0, state.damageCooldown - safeDelta);
    state.damageFlash = Math.max(0, state.damageFlash - safeDelta * 1.7);
    updateParticles(safeDelta);

    if (state.paused || state.mode === "intro") return;
    if (state.mode === "win" || state.mode === "lost") return;

    if (state.phase === "escape") {
      state.alarmTime -= safeDelta;
      if (state.alarmTime <= 0) {
        state.alarmTime = 0;
        lose("ประตูทางออกปิดก่อนหลบหนีสำเร็จ");
        return;
      }
    } else {
      state.missionTime -= safeDelta;
      if (state.missionTime <= 0) {
        state.missionTime = 0;
        lose("หมดเวลาแทรกซึม");
        return;
      }
    }

    if (state.mode === "hacking") {
      updateHack(safeDelta);
      return;
    }

    updatePlayer(safeDelta);
    checkLaserCollisions();
    updateDrone(safeDelta);

    if (
      state.phase === "escape" &&
      state.player.y > 646 &&
      Math.abs(state.player.x - exitPosition().x) < 58
    ) {
      winGame();
    }
  }

  function drawBackdrop() {
    const background = ctx.createLinearGradient(0, 0, 0, WORLD_HEIGHT);
    background.addColorStop(0, "#070b14");
    background.addColorStop(0.48, "#040a0f");
    background.addColorStop(1, "#020306");
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);

    const glow = radialGradient(
      ctx,
      195,
      360,
      420,
      "rgba(27,129,122,.17)",
      "rgba(0,0,0,0)"
    );
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);

    rain.forEach(drop => {
      const y =
        (drop.y + state.elapsed * drop.speed + drop.phase * 13) % WORLD_HEIGHT;
      ctx.strokeStyle = `rgba(111,221,216,${drop.alpha})`;
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      ctx.moveTo(drop.x, y);
      ctx.lineTo(drop.x - 3, y + drop.length);
      ctx.stroke();
    });
  }

  function drawHeader() {
    ctx.save();
    ctx.fillStyle = "rgba(3,7,12,.82)";
    ctx.fillRect(0, 0, WORLD_WIDTH, 124);
    ctx.strokeStyle = "rgba(98,245,220,.22)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(18, 116);
    ctx.lineTo(372, 116);
    ctx.stroke();

    drawText(ctx, "N / H", 30, 29, {
      font: '900 9px "Arial Narrow", sans-serif',
      color: COLORS.cyan,
      letterSpacing: 2
    });
    drawText(ctx, "NEON HEIST 888", 195, 29, {
      font: '900 19px "Arial Narrow", "Noto Sans Thai", sans-serif',
      color: COLORS.cream,
      shadow: "rgba(255,191,87,.3)",
      shadowBlur: 16,
      letterSpacing: 1.1
    });
    drawText(ctx, "LIVE", 354, 29, {
      font: '900 8px "Arial Narrow", sans-serif',
      color: state.phase === "escape" ? COLORS.laser : COLORS.cyan,
      letterSpacing: 1
    });

    const displayedTime =
      state.phase === "escape" ? state.alarmTime : state.missionTime;
    const urgent = state.phase === "escape" || displayedTime < 10;
    drawText(
      ctx,
      `00:${String(Math.max(0, Math.ceil(displayedTime))).padStart(2, "0")}`,
      55,
      75,
      {
        font: '900 20px "Arial Narrow", sans-serif',
        color: urgent ? COLORS.laser : "#e9f8f3",
        shadow: urgent ? "rgba(255,62,114,.6)" : null,
        shadowBlur: 14
      }
    );
    drawText(ctx, "MISSION", 55, 98, {
      font: '700 6px "Arial Narrow", sans-serif',
      color: COLORS.muted,
      letterSpacing: 1.5
    });

    drawText(ctx, objectiveText(), 195, 79, {
      font: '700 10px "Arial Narrow", "Noto Sans Thai", sans-serif',
      color: "#d6e9e5"
    });
    drawText(
      ctx,
      state.phase === "escape"
        ? "EXFILTRATION IN PROGRESS"
        : state.mode === "hacking"
        ? `SECURITY LINK ${state.hack.hits}/3`
        : "STEALTH PROTOCOL",
      195,
      99,
      {
        font: '700 6px "Arial Narrow", sans-serif',
        color: state.phase === "escape" ? COLORS.laser : COLORS.cyan,
        letterSpacing: 1.3
      }
    );

    for (let index = 0; index < state.maxIntegrity; index += 1) {
      const x = 309 + index * 14;
      ctx.fillStyle =
        index < state.integrity
          ? state.damageFlash > 0
            ? COLORS.laserHot
            : COLORS.cyan
          : "rgba(93,126,127,.18)";
      ctx.shadowColor = COLORS.cyan;
      ctx.shadowBlur = index < state.integrity ? 8 : 0;
      ctx.beginPath();
      ctx.moveTo(x, 72);
      ctx.lineTo(x + 5, 79);
      ctx.lineTo(x, 86);
      ctx.lineTo(x - 5, 79);
      ctx.closePath();
      ctx.fill();
    }
    ctx.shadowBlur = 0;
    drawText(ctx, "INTEGRITY", 338, 99, {
      font: '700 6px "Arial Narrow", sans-serif',
      color: COLORS.muted,
      letterSpacing: 1.2
    });
    ctx.restore();
  }

  function drawFloor() {
    ctx.save();
    roundRect(ctx, 15, 127, 360, 579, 26);
    ctx.clip();

    const floor = ctx.createLinearGradient(0, 127, 0, 706);
    floor.addColorStop(0, "#0b2026");
    floor.addColorStop(0.45, "#07171c");
    floor.addColorStop(1, "#061014");
    ctx.fillStyle = floor;
    ctx.fillRect(15, 127, 360, 579);

    ctx.strokeStyle = "rgba(82,193,184,.075)";
    ctx.lineWidth = 1;
    for (let x = 18; x <= 374; x += 32) {
      ctx.beginPath();
      ctx.moveTo(x, 127);
      ctx.lineTo(x, 706);
      ctx.stroke();
    }
    for (let y = 135; y <= 706; y += 32) {
      ctx.beginPath();
      ctx.moveTo(15, y);
      ctx.lineTo(375, y);
      ctx.stroke();
    }

    const scanY = 127 + ((state.elapsed * 46) % 579);
    const scan = ctx.createLinearGradient(0, scanY - 38, 0, scanY + 38);
    scan.addColorStop(0, "rgba(98,245,220,0)");
    scan.addColorStop(0.5, "rgba(98,245,220,.055)");
    scan.addColorStop(1, "rgba(98,245,220,0)");
    ctx.fillStyle = scan;
    ctx.fillRect(15, scanY - 38, 360, 76);

    ctx.strokeStyle = "rgba(255,191,87,.12)";
    ctx.setLineDash([7, 11]);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(195, 676);
    ctx.lineTo(195, 188);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    ctx.strokeStyle = "rgba(116,225,214,.24)";
    ctx.lineWidth = 1.3;
    roundRect(ctx, 15, 127, 360, 579, 26);
    ctx.stroke();
  }

  function drawWall(rect) {
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,.8)";
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 7;
    const side = ctx.createLinearGradient(
      rect.x,
      rect.y,
      rect.x,
      rect.y + rect.height
    );
    side.addColorStop(0, "#21313a");
    side.addColorStop(1, "#091016");
    ctx.fillStyle = side;
    roundRect(ctx, rect.x, rect.y, rect.width, rect.height, 5);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
    ctx.fillStyle = "rgba(117,151,157,.18)";
    roundRect(ctx, rect.x + 2, rect.y + 2, rect.width - 4, 5, 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(130,189,189,.18)";
    ctx.lineWidth = 1;
    roundRect(ctx, rect.x, rect.y, rect.width, rect.height, 5);
    ctx.stroke();
    ctx.restore();
  }

  function drawExit() {
    const exit = exitPosition();
    const active = state.phase === "escape";
    const pulse = 0.5 + 0.5 * Math.sin(state.elapsed * 6);
    ctx.save();
    ctx.translate(exit.x, exit.y);
    ctx.fillStyle = active
      ? `rgba(98,245,220,${0.12 + pulse * 0.15})`
      : "rgba(58,104,105,.08)";
    ctx.beginPath();
    ctx.ellipse(0, 0, 58, 20, 0, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = active
      ? `rgba(98,245,220,${0.52 + pulse * 0.35})`
      : "rgba(93,126,127,.22)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(0, 0, 49, 15, 0, 0, TAU);
    ctx.stroke();
    drawText(ctx, active ? "EXTRACT" : "ENTRY", 0, 1, {
      font: '900 8px "Arial Narrow", sans-serif',
      color: active ? COLORS.cyan : COLORS.muted,
      letterSpacing: 1.5
    });
    ctx.restore();
  }

  function drawTerminal() {
    const terminal = terminalPosition();
    const active = state.phase === "infiltrate";
    const pulse = 0.5 + 0.5 * Math.sin(state.elapsed * 4.6);
    ctx.save();
    ctx.translate(terminal.x, terminal.y);
    ctx.shadowColor = active ? COLORS.amber : COLORS.cyan;
    ctx.shadowBlur = active ? 12 + pulse * 10 : 8;
    ctx.fillStyle = "#071015";
    roundRect(ctx, -24, -25, 48, 50, 8);
    ctx.fill();
    ctx.strokeStyle = active ? COLORS.amber : COLORS.cyanDark;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.fillStyle = active ? "rgba(255,191,87,.18)" : "rgba(98,245,220,.12)";
    roundRect(ctx, -17, -16, 34, 23, 4);
    ctx.fill();
    ctx.strokeStyle = active ? COLORS.amber : COLORS.cyan;
    ctx.beginPath();
    ctx.moveTo(-11, -4);
    ctx.lineTo(-4, -10);
    ctx.lineTo(3, -2);
    ctx.lineTo(11, -8);
    ctx.stroke();
    drawText(ctx, state.terminalUnlocked ? "OPEN" : "HACK", 0, 16, {
      font: '900 6px "Arial Narrow", sans-serif',
      color: active ? COLORS.amber : COLORS.cyan,
      letterSpacing: 1
    });
    ctx.restore();
  }

  function drawDoor() {
    const y = 296;
    ctx.save();
    if (!state.terminalUnlocked) {
      ctx.shadowColor = COLORS.laser;
      ctx.shadowBlur = 16;
      ctx.strokeStyle = "rgba(255,62,114,.82)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(20, y + 9);
      ctx.lineTo(370, y + 9);
      ctx.stroke();
      ctx.shadowBlur = 0;
      drawText(ctx, "SECURITY LOCK", 195, y - 12, {
        font: '900 7px "Arial Narrow", sans-serif',
        color: COLORS.laser,
        letterSpacing: 2
      });
    } else {
      const pulse = 0.5 + 0.5 * Math.sin(state.elapsed * 3);
      ctx.strokeStyle = `rgba(98,245,220,${0.35 + pulse * 0.25})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(137, y + 9);
      ctx.lineTo(253, y + 9);
      ctx.stroke();
      drawText(ctx, "ACCESS GRANTED", 195, y - 11, {
        font: '900 7px "Arial Narrow", sans-serif',
        color: COLORS.cyan,
        letterSpacing: 1.7
      });
    }
    ctx.restore();
  }

  function drawArtifact() {
    if (state.artifactTaken) return;
    const artifact = artifactPosition();
    const floatY = Math.sin(state.elapsed * 2.2) * 5;
    const pulse = 0.55 + 0.45 * Math.sin(state.elapsed * 4.5);
    ctx.save();
    ctx.translate(artifact.x, artifact.y + floatY);
    ctx.shadowColor = COLORS.cyan;
    ctx.shadowBlur = 24 + pulse * 18;
    ctx.fillStyle = "rgba(98,245,220,.11)";
    ctx.beginPath();
    ctx.arc(0, 0, 32 + pulse * 5, 0, TAU);
    ctx.fill();

    const crystal = ctx.createLinearGradient(-18, -25, 18, 25);
    crystal.addColorStop(0, "#ffffff");
    crystal.addColorStop(0.28, COLORS.cyan);
    crystal.addColorStop(0.64, COLORS.violet);
    crystal.addColorStop(1, "#342463");
    ctx.fillStyle = crystal;
    ctx.beginPath();
    ctx.moveTo(0, -28);
    ctx.lineTo(20, -3);
    ctx.lineTo(7, 27);
    ctx.lineTo(-16, 12);
    ctx.lineTo(-20, -8);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = COLORS.cream;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.shadowBlur = 0;
    drawText(ctx, "PRISM 888", 0, 47, {
      font: '900 7px "Arial Narrow", sans-serif',
      color: COLORS.cream,
      letterSpacing: 1.5
    });
    ctx.restore();
  }

  function drawLaser(laser) {
    if (!laser.active) {
      if (laser.type === "vertical") {
        ctx.fillStyle = "rgba(255,62,114,.14)";
        ctx.beginPath();
        ctx.arc(laser.x, laser.y1, 5, 0, TAU);
        ctx.fill();
      }
      return;
    }
    const pulse = 0.72 + Math.sin(state.elapsed * 14) * 0.18;
    ctx.save();
    ctx.shadowColor = COLORS.laser;
    ctx.shadowBlur = 13;
    ctx.strokeStyle = `rgba(255,62,114,${pulse})`;
    ctx.lineWidth = state.phase === "escape" ? 3 : 2;
    if (laser.type === "horizontal") {
      const gapStart = laser.gapCenter - laser.gapWidth / 2;
      const gapEnd = laser.gapCenter + laser.gapWidth / 2;
      ctx.beginPath();
      ctx.moveTo(laser.x1, laser.y);
      ctx.lineTo(gapStart, laser.y);
      ctx.moveTo(gapEnd, laser.y);
      ctx.lineTo(laser.x2, laser.y);
      ctx.stroke();

      [laser.x1, laser.x2].forEach(x => {
        ctx.fillStyle = COLORS.laserHot;
        ctx.beginPath();
        ctx.arc(x, laser.y, 3.4, 0, TAU);
        ctx.fill();
      });
      ctx.shadowBlur = 0;
      ctx.strokeStyle = "rgba(98,245,220,.18)";
      ctx.setLineDash([2, 5]);
      ctx.beginPath();
      ctx.moveTo(gapStart, laser.y);
      ctx.lineTo(gapEnd, laser.y);
      ctx.stroke();
      ctx.setLineDash([]);
    } else {
      ctx.beginPath();
      ctx.moveTo(laser.x, laser.y1);
      ctx.lineTo(laser.x, laser.y2);
      ctx.stroke();
      ctx.fillStyle = COLORS.laserHot;
      ctx.beginPath();
      ctx.arc(laser.x, laser.y1, 3.4, 0, TAU);
      ctx.arc(laser.x, laser.y2, 3.4, 0, TAU);
      ctx.fill();
    }
    ctx.restore();
  }

  function drawDrone() {
    if (!state.drone.active) return;
    const drone = state.drone;
    ctx.save();
    ctx.translate(drone.x, drone.y);
    ctx.shadowColor = COLORS.laser;
    ctx.shadowBlur = 15 + drone.pulse * 10;
    ctx.fillStyle = "#131118";
    ctx.beginPath();
    ctx.arc(0, 0, drone.radius, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = COLORS.laser;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = COLORS.laserHot;
    ctx.beginPath();
    ctx.arc(0, 0, 3 + drone.pulse * 1.5, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = "rgba(255,62,114,.65)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(-20, 0);
    ctx.lineTo(-11, 0);
    ctx.moveTo(11, 0);
    ctx.lineTo(20, 0);
    ctx.stroke();
    ctx.restore();
  }

  function drawPlayer() {
    const player = state.player;
    ctx.save();
    const cone = ctx.createLinearGradient(
      player.x,
      player.y,
      player.x + Math.cos(player.angle) * 95,
      player.y + Math.sin(player.angle) * 95
    );
    cone.addColorStop(0, "rgba(98,245,220,.15)");
    cone.addColorStop(1, "rgba(98,245,220,0)");
    ctx.fillStyle = cone;
    ctx.beginPath();
    ctx.moveTo(player.x, player.y);
    ctx.arc(player.x, player.y, 92, player.angle - 0.26, player.angle + 0.26);
    ctx.closePath();
    ctx.fill();

    ctx.translate(player.x, player.y);
    ctx.rotate(player.angle + Math.PI / 2);
    ctx.shadowColor =
      state.damageFlash > 0 ? COLORS.laser : "rgba(98,245,220,.55)";
    ctx.shadowBlur = 13;
    const body = ctx.createLinearGradient(-10, -14, 10, 15);
    body.addColorStop(0, "#263943");
    body.addColorStop(0.55, "#0b1218");
    body.addColorStop(1, "#020507");
    ctx.fillStyle = body;
    ctx.beginPath();
    ctx.moveTo(0, -15);
    ctx.lineTo(11, 10);
    ctx.lineTo(0, 15);
    ctx.lineTo(-11, 10);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = state.damageFlash > 0 ? COLORS.laserHot : COLORS.cyan;
    ctx.lineWidth = 1.4;
    ctx.stroke();
    ctx.fillStyle = COLORS.cyan;
    roundRect(ctx, -7, -5, 14, 4, 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.restore();
  }

  function drawParticles() {
    ctx.save();
    state.particles.forEach(particle => {
      const alpha = clamp(particle.life / particle.maxLife, 0, 1);
      ctx.globalAlpha = alpha;
      ctx.shadowColor = particle.color;
      ctx.shadowBlur = 8;
      ctx.fillStyle = particle.color;
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, TAU);
      ctx.fill();
    });
    ctx.restore();
  }

  function drawMap() {
    drawFloor();
    drawExit();
    getWalls().forEach(drawWall);
    drawDoor();
    drawTerminal();
    drawArtifact();
    getLaserState().forEach(drawLaser);
    drawDrone();
    drawPlayer();
    drawParticles();
  }

  function drawJoystick() {
    const centerX = 73;
    const centerY = 769;
    const vectorX = state.joystick.active ? state.joystick.x * 23 : 0;
    const vectorY = state.joystick.active ? state.joystick.y * 23 : 0;
    ctx.save();
    ctx.fillStyle = "rgba(8,18,25,.72)";
    ctx.beginPath();
    ctx.arc(centerX, centerY, 46, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = "rgba(98,245,220,.28)";
    ctx.lineWidth = 1.2;
    ctx.stroke();
    ctx.setLineDash([3, 7]);
    ctx.beginPath();
    ctx.arc(centerX, centerY, 33, 0, TAU);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.shadowColor = COLORS.cyan;
    ctx.shadowBlur = state.joystick.active ? 13 : 0;
    ctx.fillStyle = state.joystick.active
      ? "rgba(98,245,220,.78)"
      : "rgba(89,126,128,.35)";
    ctx.beginPath();
    ctx.arc(centerX + vectorX, centerY + vectorY, 18, 0, TAU);
    ctx.fill();
    ctx.shadowBlur = 0;
    drawText(ctx, "MOVE", centerX, 824, {
      font: '700 6px "Arial Narrow", sans-serif',
      color: COLORS.muted,
      letterSpacing: 1.4
    });
    ctx.restore();
  }

  function drawActionButton() {
    const x = 319;
    const y = 769;
    const active =
      Boolean(state.prompt) ||
      state.mode === "hacking" ||
      state.mode === "intro" ||
      state.mode === "win" ||
      state.mode === "lost";
    const pulse = 0.5 + 0.5 * Math.sin(state.elapsed * 5);
    const label =
      state.mode === "hacking"
        ? "SYNC"
        : state.prompt || (state.phase === "escape" ? "RUN" : "ACT");

    ctx.save();
    ctx.shadowColor = state.mode === "hacking" ? COLORS.amber : COLORS.cyan;
    ctx.shadowBlur = active ? 14 + pulse * 9 : 0;
    ctx.fillStyle = active
      ? state.mode === "hacking"
        ? "rgba(255,191,87,.22)"
        : "rgba(98,245,220,.18)"
      : "rgba(22,39,46,.65)";
    ctx.beginPath();
    ctx.arc(x, y, 43, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = active
      ? state.mode === "hacking"
        ? COLORS.amber
        : COLORS.cyan
      : "rgba(106,139,140,.28)";
    ctx.lineWidth = active ? 2 : 1;
    ctx.stroke();
    ctx.shadowBlur = 0;
    drawText(ctx, label, x, y, {
      font: '900 12px "Arial Narrow", sans-serif',
      color: active ? COLORS.cream : COLORS.muted,
      letterSpacing: 1.2
    });
    drawText(ctx, "ACTION", x, 824, {
      font: '700 6px "Arial Narrow", sans-serif',
      color: COLORS.muted,
      letterSpacing: 1.4
    });
    ctx.restore();
  }

  function drawControls() {
    ctx.fillStyle = "rgba(2,4,8,.86)";
    ctx.fillRect(0, 704, WORLD_WIDTH, 140);
    ctx.strokeStyle = "rgba(98,245,220,.12)";
    ctx.beginPath();
    ctx.moveTo(0, 705);
    ctx.lineTo(WORLD_WIDTH, 705);
    ctx.stroke();
    drawJoystick();
    drawActionButton();

    if (state.message && state.mode !== "intro") {
      ctx.fillStyle = "rgba(4,10,14,.76)";
      roundRect(ctx, 117, 718, 156, 31, 11);
      ctx.fill();
      drawText(ctx, state.message, 195, 734, {
        font: '700 8px "Arial Narrow", "Noto Sans Thai", sans-serif',
        color:
          state.damageFlash > 0 || state.phase === "escape"
            ? COLORS.laserHot
            : "#b8cfca"
      });
    }
  }

  function drawHackOverlay() {
    if (state.mode !== "hacking") return;
    const index = Math.min(state.hack.hits, HACK_TARGETS.length - 1);
    const target = HACK_TARGETS[index];
    const zoneWidth = HACK_ZONE_WIDTHS[index];
    const railX = 49;
    const railY = 430;
    const railWidth = 292;
    const targetX = railX + target * railWidth;
    const zonePixels = zoneWidth * railWidth;
    const cursorX = railX + state.hack.cursor * railWidth;

    ctx.save();
    ctx.fillStyle = "rgba(1,4,8,.91)";
    ctx.fillRect(0, 118, WORLD_WIDTH, 586);
    const glow = radialGradient(
      ctx,
      195,
      400,
      260,
      "rgba(27,125,117,.18)",
      "rgba(0,0,0,0)"
    );
    ctx.fillStyle = glow;
    ctx.fillRect(0, 118, WORLD_WIDTH, 586);

    drawText(ctx, "SECURITY UPLINK", 195, 190, {
      font: '900 9px "Arial Narrow", sans-serif',
      color: COLORS.cyan,
      letterSpacing: 3
    });
    drawText(ctx, "จับจังหวะสัญญาณ", 195, 235, {
      font: '900 27px "Arial Narrow", "Noto Sans Thai", sans-serif',
      color: COLORS.cream
    });
    drawText(ctx, "แตะ SYNC เมื่อเส้นวิ่งอยู่ในช่องสีทอง", 195, 269, {
      font: '400 11px "Arial Narrow", "Noto Sans Thai", sans-serif',
      color: "#9db7b5"
    });

    for (let step = 0; step < 3; step += 1) {
      const x = 147 + step * 48;
      ctx.fillStyle =
        step < state.hack.hits ? COLORS.cyan : "rgba(91,126,128,.18)";
      ctx.shadowColor = COLORS.cyan;
      ctx.shadowBlur = step < state.hack.hits ? 10 : 0;
      ctx.beginPath();
      ctx.arc(x, 320, 8, 0, TAU);
      ctx.fill();
      ctx.shadowBlur = 0;
      if (step < 2) {
        ctx.strokeStyle =
          step < state.hack.hits - 1 ? COLORS.cyan : "rgba(91,126,128,.18)";
        ctx.beginPath();
        ctx.moveTo(x + 11, 320);
        ctx.lineTo(x + 37, 320);
        ctx.stroke();
      }
    }

    ctx.fillStyle = "rgba(10,24,30,.94)";
    roundRect(ctx, railX - 10, railY - 28, railWidth + 20, 82, 17);
    ctx.fill();
    ctx.strokeStyle = "rgba(98,245,220,.24)";
    ctx.stroke();

    ctx.fillStyle = "rgba(82,111,114,.22)";
    roundRect(ctx, railX, railY, railWidth, 10, 5);
    ctx.fill();
    ctx.shadowColor = COLORS.amber;
    ctx.shadowBlur = 15;
    ctx.fillStyle = "rgba(255,191,87,.85)";
    roundRect(ctx, targetX - zonePixels / 2, railY - 4, zonePixels, 18, 7);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.shadowColor = COLORS.cyan;
    ctx.shadowBlur = 16;
    ctx.strokeStyle = COLORS.cream;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(cursorX, railY - 18);
    ctx.lineTo(cursorX, railY + 29);
    ctx.stroke();
    ctx.fillStyle = COLORS.cyan;
    ctx.beginPath();
    ctx.moveTo(cursorX, railY - 22);
    ctx.lineTo(cursorX - 7, railY - 32);
    ctx.lineTo(cursorX + 7, railY - 32);
    ctx.closePath();
    ctx.fill();
    ctx.shadowBlur = 0;

    drawText(
      ctx,
      state.hack.result === "MISS"
        ? "SIGNAL REJECTED"
        : `SYNC CHANNEL ${state.hack.hits + 1}/3`,
      195,
      519,
      {
        font: '900 9px "Arial Narrow", sans-serif',
        color: state.hack.result === "MISS" ? COLORS.laser : COLORS.cyan,
        letterSpacing: 1.8
      }
    );
    drawText(ctx, `SECURITY NOISE ${state.hack.misses}`, 195, 551, {
      font: '700 7px "Arial Narrow", sans-serif',
      color: COLORS.muted,
      letterSpacing: 1.2
    });
    ctx.restore();
  }

  function drawIntro() {
    if (state.mode !== "intro") return;
    ctx.save();
    ctx.fillStyle = "rgba(1,3,7,.82)";
    ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    const spotlight = radialGradient(
      ctx,
      195,
      400,
      360,
      "rgba(20,119,111,.25)",
      "rgba(0,0,0,.9)"
    );
    ctx.fillStyle = spotlight;
    ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);

    ctx.save();
    ctx.translate(195, 137);
    ctx.rotate(-0.08);
    ctx.strokeStyle = "rgba(98,245,220,.35)";
    ctx.lineWidth = 1;
    roundRect(ctx, -82, -18, 164, 36, 5);
    ctx.stroke();
    drawText(ctx, "CLASSIFIED / 888", 0, 0, {
      font: '900 8px "Arial Narrow", sans-serif',
      color: COLORS.cyan,
      letterSpacing: 2.2
    });
    ctx.restore();

    drawText(ctx, "NEON", 195, 210, {
      font: '900 56px "Arial Narrow", "Noto Sans Thai", sans-serif',
      color: "#f3fbf8",
      shadow: "rgba(98,245,220,.45)",
      shadowBlur: 24,
      letterSpacing: 3
    });
    drawText(ctx, "HEIST 888", 195, 265, {
      font: '900 39px "Arial Narrow", "Noto Sans Thai", sans-serif',
      color: COLORS.amber,
      shadow: "rgba(255,191,87,.44)",
      shadowBlur: 22,
      letterSpacing: 1.2
    });
    drawText(ctx, "แทรกซึม · แฮก · ชิงเพชร · หลบหนี", 195, 311, {
      font: '700 11px "Arial Narrow", "Noto Sans Thai", sans-serif',
      color: "#c3d8d4"
    });

    const missions = [
      { label: "ฝ่าเลเซอร์", code: "01", color: COLORS.laser },
      { label: "แฮกระบบ", code: "02", color: COLORS.cyan },
      { label: "ขโมย PRISM", code: "03", color: COLORS.amber }
    ];
    missions.forEach((mission, index) => {
      const y = 383 + index * 70;
      ctx.fillStyle = "rgba(8,21,28,.72)";
      roundRect(ctx, 52, y, 286, 52, 12);
      ctx.fill();
      ctx.strokeStyle = "rgba(104,163,159,.18)";
      ctx.stroke();
      drawText(ctx, mission.code, 79, y + 26, {
        font: '900 10px "Arial Narrow", sans-serif',
        color: mission.color,
        letterSpacing: 1
      });
      ctx.fillStyle = mission.color;
      ctx.fillRect(102, y + 14, 2, 24);
      drawText(ctx, mission.label, 121, y + 21, {
        font: '900 13px "Arial Narrow", "Noto Sans Thai", sans-serif',
        color: "#ecf6f3",
        align: "left"
      });
      drawText(
        ctx,
        index === 0
          ? "หาแนวช่องว่างและจับจังหวะ"
          : index === 1
          ? "แตะให้ตรงสัญญาณ 3 ครั้ง"
          : "หนีออกก่อนเวลาหมด",
        121,
        y + 37,
        {
          font: '400 8px "Arial Narrow", "Noto Sans Thai", sans-serif',
          color: "#769694",
          align: "left"
        }
      );
    });

    const pulse = 0.62 + Math.sin(state.elapsed * 3.3) * 0.13;
    ctx.save();
    ctx.shadowColor = COLORS.cyan;
    ctx.shadowBlur = 22 * pulse;
    const button = ctx.createLinearGradient(0, 616, 0, 682);
    button.addColorStop(0, "#85ffe7");
    button.addColorStop(0.55, "#39bdaa");
    button.addColorStop(1, "#167d74");
    ctx.fillStyle = button;
    roundRect(ctx, 57, 618, 276, 66, 19);
    ctx.fill();
    ctx.strokeStyle = "#c8fff3";
    ctx.lineWidth = 1.2;
    ctx.stroke();
    ctx.restore();
    drawText(ctx, "เริ่มปฏิบัติการ", 195, 645, {
      font: '900 17px "Arial Narrow", "Noto Sans Thai", sans-serif',
      color: "#04110e"
    });
    drawText(ctx, "TAP TO INFILTRATE", 195, 668, {
      font: '900 7px "Arial Narrow", sans-serif',
      color: "rgba(3,27,22,.72)",
      letterSpacing: 2.1
    });
    drawText(ctx, "ลากจอยเพื่อเคลื่อนที่ · แตะ ACTION เพื่อโต้ตอบ", 195, 731, {
      font: '400 9px "Arial Narrow", "Noto Sans Thai", sans-serif',
      color: "#6f8d8b"
    });
    drawText(ctx, "INTERACTIVE PREVIEW · NO REAL REWARD", 195, 796, {
      font: '700 6px "Arial Narrow", sans-serif',
      color: "rgba(112,143,142,.55)",
      letterSpacing: 1.2
    });
    ctx.restore();
  }

  function drawWin() {
    if (state.mode !== "win") return;
    const pulse = 1 + Math.sin(state.elapsed * 4) * 0.02;
    ctx.save();
    ctx.fillStyle = "rgba(1,4,8,.88)";
    ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    const glow = radialGradient(
      ctx,
      195,
      362,
      285,
      "rgba(98,245,220,.25)",
      "rgba(0,0,0,0)"
    );
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    ctx.translate(195, 370);
    ctx.scale(pulse, pulse);
    ctx.translate(-195, -370);

    drawText(ctx, "MISSION COMPLETE", 195, 166, {
      font: '900 9px "Arial Narrow", sans-serif',
      color: COLORS.cyan,
      shadow: "rgba(98,245,220,.75)",
      shadowBlur: 15,
      letterSpacing: 3
    });
    drawText(ctx, "PRISM SECURED", 195, 215, {
      font: '900 31px "Arial Narrow", sans-serif',
      color: COLORS.cream,
      letterSpacing: 1
    });

    ctx.save();
    ctx.translate(195, 348);
    ctx.rotate(state.elapsed * 0.18);
    ctx.shadowColor = COLORS.cyan;
    ctx.shadowBlur = 38;
    const crystal = ctx.createLinearGradient(-36, -55, 36, 55);
    crystal.addColorStop(0, "#ffffff");
    crystal.addColorStop(0.25, COLORS.cyan);
    crystal.addColorStop(0.66, COLORS.violet);
    crystal.addColorStop(1, "#2c1d5b");
    ctx.fillStyle = crystal;
    ctx.beginPath();
    ctx.moveTo(0, -64);
    ctx.lineTo(44, -10);
    ctx.lineTo(16, 61);
    ctx.lineTo(-37, 31);
    ctx.lineTo(-44, -22);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = COLORS.cream;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();

    drawText(ctx, state.completedScore.toLocaleString(), 195, 477, {
      font: '900 39px "Arial Narrow", sans-serif',
      color: COLORS.amber,
      shadow: "rgba(255,191,87,.55)",
      shadowBlur: 22
    });
    drawText(ctx, "MISSION SCORE", 195, 510, {
      font: '900 7px "Arial Narrow", sans-serif',
      color: COLORS.muted,
      letterSpacing: 2
    });

    ctx.fillStyle = "rgba(10,28,31,.7)";
    roundRect(ctx, 72, 548, 246, 48, 14);
    ctx.fill();
    drawText(
      ctx,
      `INTEGRITY ${state.integrity}/${state.maxIntegrity}  ·  NOISE ${state.hack.misses}`,
      195,
      572,
      {
        font: '900 8px "Arial Narrow", sans-serif',
        color: COLORS.cyan,
        letterSpacing: 1
      }
    );

    const button = ctx.createLinearGradient(0, 635, 0, 699);
    button.addColorStop(0, "#ffd27a");
    button.addColorStop(1, "#b67827");
    ctx.fillStyle = button;
    roundRect(ctx, 64, 634, 262, 65, 19);
    ctx.fill();
    ctx.strokeStyle = COLORS.cream;
    ctx.stroke();
    drawText(ctx, "รับภารกิจใหม่", 195, 666, {
      font: '900 16px "Arial Narrow", "Noto Sans Thai", sans-serif',
      color: "#1e1203"
    });
    drawText(ctx, "Preview เท่านั้น ไม่มีการเพิ่มเครดิตจริง", 195, 745, {
      font: '400 9px "Arial Narrow", "Noto Sans Thai", sans-serif',
      color: "#7e9d99"
    });
    ctx.restore();
  }

  function drawLost() {
    if (state.mode !== "lost") return;
    ctx.save();
    ctx.fillStyle = "rgba(8,1,5,.91)";
    ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    const glow = radialGradient(
      ctx,
      195,
      350,
      290,
      "rgba(255,62,114,.18)",
      "rgba(0,0,0,0)"
    );
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    drawText(ctx, "SECURITY BREACH", 195, 188, {
      font: '900 10px "Arial Narrow", sans-serif',
      color: COLORS.laser,
      shadow: "rgba(255,62,114,.7)",
      shadowBlur: 18,
      letterSpacing: 2.8
    });
    drawText(ctx, "ภารกิจล้มเหลว", 195, 251, {
      font: '900 35px "Arial Narrow", "Noto Sans Thai", sans-serif',
      color: "#fff2f5"
    });

    ctx.strokeStyle = "rgba(255,62,114,.45)";
    ctx.lineWidth = 1;
    for (let index = 0; index < 5; index += 1) {
      const y = 318 + index * 33;
      ctx.beginPath();
      ctx.moveTo(55 + index * 10, y);
      ctx.lineTo(335 - index * 8, y);
      ctx.stroke();
    }
    drawText(ctx, state.message, 195, 505, {
      font: '700 12px "Arial Narrow", "Noto Sans Thai", sans-serif',
      color: "#d9b8c1"
    });
    drawText(
      ctx,
      `SCORE ${state.score.toLocaleString()} · HACK MISS ${state.hack.misses}`,
      195,
      543,
      {
        font: '900 8px "Arial Narrow", sans-serif',
        color: COLORS.muted,
        letterSpacing: 1.3
      }
    );
    ctx.fillStyle = COLORS.laser;
    roundRect(ctx, 64, 625, 262, 65, 19);
    ctx.fill();
    ctx.strokeStyle = COLORS.laserHot;
    ctx.stroke();
    drawText(ctx, "ลองใหม่", 195, 657, {
      font: '900 17px "Arial Narrow", "Noto Sans Thai", sans-serif',
      color: "#21040d"
    });
    ctx.restore();
  }

  function drawPaused() {
    if (!state.paused) return;
    ctx.fillStyle = "rgba(1,4,8,.86)";
    ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    drawText(ctx, "PAUSED", 195, 380, {
      font: '900 37px "Arial Narrow", sans-serif',
      color: COLORS.cream,
      letterSpacing: 3
    });
    drawText(ctx, "กด P เพื่อเล่นต่อ", 195, 428, {
      font: '700 11px "Arial Narrow", "Noto Sans Thai", sans-serif',
      color: COLORS.cyan
    });
  }

  function render() {
    if (destroyed) return;
    ctx.setTransform(viewport.dpr, 0, 0, viewport.dpr, 0, 0);
    ctx.clearRect(0, 0, viewport.width, viewport.height);
    ctx.fillStyle = COLORS.void;
    ctx.fillRect(0, 0, viewport.width, viewport.height);
    ctx.save();
    ctx.translate(viewport.offsetX, viewport.offsetY);
    ctx.scale(viewport.scale, viewport.scale);
    drawBackdrop();
    drawHeader();
    drawMap();
    drawControls();
    drawHackOverlay();
    drawIntro();
    drawWin();
    drawLost();
    drawPaused();
    if (state.damageFlash > 0 && state.mode === "playing") {
      ctx.fillStyle = `rgba(255,62,114,${state.damageFlash * 0.12})`;
      ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    }
    ctx.restore();
  }

  function onPointerDown(event) {
    event.preventDefault();
    audio.unlock();
    const point = toWorld(event.clientX, event.clientY);
    if (canvas.setPointerCapture) {
      try {
        canvas.setPointerCapture(event.pointerId);
      } catch (error) {
        // Pointer capture is optional in older embedded webviews.
      }
    }

    if (state.mode === "intro" && pointInRect(point, 45, 590, 300, 120)) {
      startGame();
      return;
    }
    if (
      (state.mode === "win" || state.mode === "lost") &&
      pointInRect(point, 45, 600, 300, 120)
    ) {
      startGame();
      return;
    }
    if (pointInCircle(point, 319, 769, 55)) {
      performAction();
      return;
    }
    if (state.mode === "playing" && pointInCircle(point, 73, 769, 68)) {
      state.joystick.active = true;
      state.joystick.pointerId = event.pointerId;
      updateJoystick(point);
    }
  }

  function updateJoystick(point) {
    const dx = point.x - 73;
    const dy = point.y - 769;
    const length = Math.max(1, Math.hypot(dx, dy));
    const strength = clamp(length / 34, 0, 1);
    state.joystick.x = (dx / length) * strength;
    state.joystick.y = (dy / length) * strength;
  }

  function onPointerMove(event) {
    if (
      !state.joystick.active ||
      state.joystick.pointerId !== event.pointerId
    ) {
      return;
    }
    event.preventDefault();
    updateJoystick(toWorld(event.clientX, event.clientY));
  }

  function onPointerUp(event) {
    if (state.joystick.active && state.joystick.pointerId === event.pointerId) {
      state.joystick.active = false;
      state.joystick.pointerId = null;
      state.joystick.x = 0;
      state.joystick.y = 0;
    }
    if (canvas.releasePointerCapture && event.pointerId != null) {
      try {
        canvas.releasePointerCapture(event.pointerId);
      } catch (error) {
        // Pointer may already have been released by the host.
      }
    }
  }

  function onKeyDown(event) {
    const key = event.key.toLowerCase();
    if (key === "f") {
      event.preventDefault();
      if (
        !document.fullscreenElement &&
        canvas.parentElement.requestFullscreen
      ) {
        canvas.parentElement.requestFullscreen();
      } else if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen();
      }
      return;
    }
    if (key === "p" && state.mode !== "intro") {
      event.preventDefault();
      state.paused = !state.paused;
      return;
    }
    if (key === "r") {
      event.preventDefault();
      startGame();
      return;
    }
    if (key === " " || key === "enter") {
      event.preventDefault();
      if (!event.repeat) performAction();
      return;
    }
    if (
      [
        "arrowleft",
        "arrowright",
        "arrowup",
        "arrowdown",
        "w",
        "a",
        "s",
        "d"
      ].includes(key)
    ) {
      event.preventDefault();
      keys.add(key);
    }
  }

  function onKeyUp(event) {
    keys.delete(event.key.toLowerCase());
  }

  function frame(timestamp) {
    if (destroyed) return;
    const delta = (timestamp - previousTimestamp) / 1000;
    previousTimestamp = timestamp;
    update(delta);
    render();
    animationFrame = requestAnimationFrame(frame);
  }

  function renderGameToText() {
    const lasers = getLaserState().map(laser => {
      if (laser.type === "horizontal") {
        return {
          id: laser.id,
          type: laser.type,
          y: Math.round(laser.y),
          gapCenterX: Math.round(laser.gapCenter),
          gapWidth: laser.gapWidth,
          active: laser.active
        };
      }
      return {
        id: laser.id,
        type: laser.type,
        x: Math.round(laser.x),
        y1: laser.y1,
        y2: laser.y2,
        active: laser.active
      };
    });
    return JSON.stringify({
      coordinateSystem:
        "logical portrait canvas 390x844; origin top-left; x right, y down; playable floor x=15..375,y=127..706",
      mode: state.mode,
      phase: state.phase,
      paused: state.paused,
      objective: objectiveText(),
      message: state.message,
      missionTime: Number(state.missionTime.toFixed(1)),
      alarmTime:
        state.phase === "escape" ? Number(state.alarmTime.toFixed(1)) : null,
      integrity: state.integrity,
      score: state.score,
      player: {
        x: Math.round(state.player.x),
        y: Math.round(state.player.y),
        radius: state.player.radius,
        moving: state.player.moving
      },
      terminal: {
        x: terminalPosition().x,
        y: terminalPosition().y,
        unlocked: state.terminalUnlocked,
        actionAvailable: state.prompt === "HACK"
      },
      artifact: {
        x: artifactPosition().x,
        y: artifactPosition().y,
        taken: state.artifactTaken,
        actionAvailable: state.prompt === "TAKE"
      },
      exit: {
        x: exitPosition().x,
        y: exitPosition().y,
        active: state.phase === "escape"
      },
      hack:
        state.mode === "hacking"
          ? {
              hits: state.hack.hits,
              misses: state.hack.misses,
              cursor: Number(state.hack.cursor.toFixed(3)),
              target: HACK_TARGETS[state.hack.hits],
              zoneWidth: HACK_ZONE_WIDTHS[state.hack.hits]
            }
          : null,
      drone: state.drone.active
        ? {
            x: Math.round(state.drone.x),
            y: Math.round(state.drone.y)
          }
        : null,
      lasers,
      controls:
        "Move: joystick at x=73,y=769 or Arrow/WASD. Action: x=319,y=769 or Space. P pause, R restart, F fullscreen."
    });
  }

  canvas.addEventListener("pointerdown", onPointerDown);
  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerup", onPointerUp);
  canvas.addEventListener("pointercancel", onPointerUp);
  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);
  window.addEventListener("resize", resize);

  window["render_game_to_text"] = renderGameToText;
  window.advanceTime = milliseconds => {
    const seconds = Math.max(0, Number(milliseconds) || 0) / 1000;
    const steps = Math.max(1, Math.ceil(seconds / (1 / 60)));
    const step = seconds / steps;
    for (let index = 0; index < steps; index += 1) update(step);
    render();
    return Promise.resolve();
  };

  resize();
  animationFrame = requestAnimationFrame(frame);

  return {
    resize,
    renderGameToText,
    destroy() {
      destroyed = true;
      cancelAnimationFrame(animationFrame);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerUp);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("resize", resize);
      if (window["render_game_to_text"] === renderGameToText) {
        delete window["render_game_to_text"];
      }
      audio.destroy();
    }
  };
}
